import Link from "next/link";
import React from "react";
import SiteShell, { APP_URL } from "@/components/SiteShell";
import FoundingMemberBanner from "@/components/FoundingMemberBanner";
import { SITE_URL } from "@/lib/structuredData";
import { TEMPLATES, templatesByCategory, templateStats } from "@/templates";

const CATEGORY_COLORS: Record<string, string> = {
  SaaS: "bg-indigo-50 text-indigo-700 border-indigo-200",
  "E-commerce": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Lead gen": "bg-amber-50 text-amber-700 border-amber-200",
  Services: "bg-sky-50 text-sky-700 border-sky-200",
  Events: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
  Coaching: "bg-rose-50 text-rose-700 border-rose-200",
  Agency: "bg-violet-50 text-violet-700 border-violet-200",
};

/** What each category is FOR — one honest line, so the jump links mean something. */
const CATEGORY_BLURBS: Record<string, string> = {
  "Lead gen": "Capture, qualify and route new leads before they cool.",
  SaaS: "Trials, activation, expansion and the two kinds of churn.",
  "E-commerce": "Carts, orders, restocks, reviews and repeat purchases.",
  Services: "Quotes, appointments, reviews and long buying cycles.",
  Events: "Webinars, workshops and shows — before, during and after.",
  Coaching: "Applications, discovery calls and getting students to finish.",
  Agency: "Client onboarding, reporting rhythm and retention signals.",
};

function anchorFor(category: string) {
  return category.toLowerCase().replace(/[^a-z0-9]+/g, "-");
}

/** Template gallery (www.frazzlecrm.com/templates) — indexable. */
export default function TemplatesIndex() {
  const groups = templatesByCategory();

  return (
    <SiteShell
      title="Funnel & automation templates — FrazzleCRM"
      description={`${TEMPLATES.length} free funnel and automation templates — lead gen, SaaS, e-commerce, services, events, coaching and agency. Every one is a real FrazzleCRM map: clone it and the diagram runs as the automation.`}
      canonical={`${SITE_URL}/templates`}
      ogTitle={`${TEMPLATES.length} free funnel templates that actually run`}
    >
      <FoundingMemberBanner />
      <section className="bg-gradient-to-b from-indigo-50/60 to-white">
        <div className="mx-auto max-w-6xl px-6 pb-10 pt-16 text-center">
          <h1 className="mx-auto max-w-3xl text-[38px] font-extrabold leading-[1.1] tracking-tight text-slate-900 sm:text-[46px]">
            {TEMPLATES.length} funnel templates that{" "}
            <span className="bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">
              actually run
            </span>
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-[16px] leading-relaxed text-slate-600">
            Every template is a real FrazzleCRM map: pages, sequences, waits and
            branches already drawn and wired. Clone one and the diagram IS the
            automation — swap in your copy and go live.
          </p>
        </div>
      </section>

      {/* Jump links. 26 cards need a table of contents, not a dropdown. */}
      <nav
        aria-label="Template categories"
        className="sticky top-0 z-10 border-b border-slate-100 bg-white/90 backdrop-blur"
      >
        <div className="mx-auto flex max-w-6xl flex-wrap gap-2 px-6 py-3">
          {groups.map((g) => (
            <a
              key={g.category}
              href={`#${anchorFor(g.category)}`}
              className={`inline-flex min-h-[44px] items-center rounded-full border px-4 text-[13px] font-semibold ${
                CATEGORY_COLORS[g.category] ??
                "border-slate-200 bg-slate-50 text-slate-600"
              }`}
            >
              {g.category}
              <span className="ml-1.5 opacity-60">{g.items.length}</span>
            </a>
          ))}
        </div>
      </nav>

      <section className="mx-auto max-w-6xl px-6 pb-20 pt-10">
        {groups.map((g) => (
          <div key={g.category} id={anchorFor(g.category)} className="mb-14 scroll-mt-20">
            <h2 className="text-[22px] font-bold tracking-tight text-slate-900">
              {g.category}
            </h2>
            <p className="mt-1 text-[14px] text-slate-500">
              {CATEGORY_BLURBS[g.category]}
            </p>
            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {g.items.map((t) => {
                const s = templateStats(t);
                return (
                  <Link
                    key={t.slug}
                    href={`/templates/${t.slug}`}
                    className="group flex flex-col rounded-2xl border border-slate-100 bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
                  >
                    <span
                      className={`w-fit rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${
                        CATEGORY_COLORS[t.category] ??
                        "border-slate-200 bg-slate-50 text-slate-600"
                      }`}
                    >
                      {t.category}
                    </span>
                    <h3 className="mt-3 text-[18px] font-bold text-slate-900 group-hover:text-indigo-700">
                      {t.name}
                    </h3>
                    <p className="mt-2 flex-1 text-[13.5px] leading-relaxed text-slate-600">
                      {t.tagline}
                    </p>
                    <div className="mt-4 flex items-center gap-3 text-[12px] text-slate-400">
                      <span>{s.nodeCount} nodes</span>
                      <span>·</span>
                      <span>
                        {s.emailCount} email{s.emailCount === 1 ? "" : "s"}
                      </span>
                      {s.smsCount > 0 && (
                        <>
                          <span>·</span>
                          <span>{s.smsCount} SMS</span>
                        </>
                      )}
                      <span className="ml-auto font-semibold text-indigo-600 group-hover:translate-x-0.5 group-hover:text-indigo-500">
                        View map →
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        ))}

        <div className="mt-4 rounded-2xl border border-slate-100 bg-slate-50/60 p-8 text-center">
          <h2 className="text-[22px] font-bold text-slate-900">
            Don&apos;t see your funnel?
          </h2>
          <p className="mx-auto mt-2 max-w-xl text-[14px] text-slate-600">
            Describe it to the{" "}
            <Link
              href="/tools/funnel-generator"
              className="font-medium text-indigo-600 hover:text-indigo-500"
            >
              free funnel map generator
            </Link>{" "}
            and it draws one — or connect Claude via MCP and it builds the map
            for you. You approve.
          </p>
          <a
            href={`${APP_URL}/signup`}
            className="mt-5 inline-block rounded-xl bg-indigo-600 px-6 py-3 text-[14px] font-semibold text-white shadow-lg shadow-indigo-200 hover:bg-indigo-500"
          >
            Start free — no credit card
          </a>
          <p className="mt-5 text-[13px] text-slate-500">
            New to funnel automation?{" "}
            <Link href="/learn" className="font-medium text-indigo-600 hover:text-indigo-500">
              The glossary
            </Link>{" "}
            defines every moving part in these maps — triggers, waits, goals,
            stop tags — and the mistake each one causes.
          </p>
          <p className="mt-5 text-[13px] text-slate-500">
            Already got a map?{" "}
            <Link href="/embed" className="font-medium text-indigo-600 hover:text-indigo-500">
              Embed it on your own site
            </Link>{" "}
            — one iframe, live and pannable, free on every plan.
          </p>
        </div>
      </section>
    </SiteShell>
  );
}
